import { defineStore } from 'pinia';
import { readonly, ref } from 'vue';

import { AlertMessage, AlertSeverity, AlertType, FullScreenAlertMessage, ModalAlertMessage, ToastAlertMessage } from './types';

const timeouts: Record<string, ReturnType<typeof setTimeout>> = {};

export const useAlertStore = defineStore('alert', () => {
  const alertMessages = ref<AlertMessage[]>([]);

  const addAlertMessage = (message: AlertMessage) => {
    const id = message.id;

    // handle existing message
    const isExisting = alertMessages.value.some((message) => message.id === id);
    if (isExisting) {
      // replace existing message
      alertMessages.value = alertMessages.value.map((existingMsg) => (existingMsg.id === id ? { ...message } : existingMsg));
      if (timeouts[id]) {
        clearTimeout(timeouts[id]);
        delete timeouts[id];
      }
    } else {
      alertMessages.value.push({ ...message });
    }

    if ('autoCloseInMs' in message && message.autoCloseInMs) {
      timeouts[id] = setTimeout(() => {
        removeAlertMessage(id);
      }, message.autoCloseInMs);
    }
  };

  const addToastMsg = (message: Omit<ToastAlertMessage, 'type' | 'severity'>) => {
    addAlertMessage({ ...message, type: AlertType.Toast, severity: AlertSeverity.Info });
  };
  const addModalMsg = (message: Omit<ModalAlertMessage, 'type' | 'severity'>) => {
    addAlertMessage({ ...message, type: AlertType.Modal, severity: AlertSeverity.Info });
  };
  const addFullScreenMsg = (message: Omit<FullScreenAlertMessage, 'type' | 'severity'>) => {
    addAlertMessage({ ...message, type: AlertType.FullScreen, severity: AlertSeverity.Info });
  };

  const removeAlertMessage = (id: string) => {
    alertMessages.value = alertMessages.value.filter((message) => message.id !== id);
  };

  const resetAlertMessage = () => {
    alertMessages.value = [];
  };

  return {
    alertMessages: readonly(alertMessages),
    removeAlertMessage,
    resetAlertMessage,
    addAlertMessage,
    addToastMsg,
    addModalMsg,
    addFullScreenMsg,
  };
});
